import { Grid } from '@mui/material';
import { LanguageType } from '@nx-smartmonkey/shared/domain';
import { HSpacer, RoutalSelect, RoutalSelectMultiSelectOption, RoutalTooltip } from '@nx-smartmonkey/ui';
import moment from 'moment';

import BarChartIcon from '../../../assets/svg/analytics-charts/BarChartIcon';
import CumulativeChartIcon from '../../../assets/svg/analytics-charts/CumulativeChartIcon';
import LineChartIcon from '../../../assets/svg/analytics-charts/LineChartIcon';
import StackChartIcon from '../../../assets/svg/analytics-charts/StackChartIcon';
import { getRootLocation, rootNavigate } from '../../../helpers/CustomRouter';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useAnalyticsFilters } from '../../../hooks/analytics/useAnalyticsFilters';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRouterQuery } from '../../../hooks/useRouterQuery';
import { BinTypes } from '../../../interfaces/analytics';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';

export const formatDateByBinType = (date: string, binType: BinTypes, language: LanguageType) => {
  const localeMoment = moment(date).locale(language);

  switch (binType) {
    case `day`:
    case `week`:
      return localeMoment.format(`MMM D, YY`);
    case `month`:
      return localeMoment.format(`MMM YYYY`);
    case `quarter`:
      return `Q${localeMoment.format(`Q/YYYY`)}`;
    default:
      return localeMoment.format(`dddd, D MMMM`);
  }
};

interface ChartHeaderFiltersProps {
  showStackedOption?: boolean;
  showCumulativeOption?: boolean;
  showBinSelector?: boolean;
}

const ChartHeaderFilters = ({
  showStackedOption = true,
  showCumulativeOption = true,
  showBinSelector = true,
}: ChartHeaderFiltersProps) => {
  const urlParams = useRouterQuery();
  const { data: activeProject } = useRetrieveActiveProject();
  const { binType, chartType, stackedChart, cumulativeChart } = useAnalyticsFilters({ project: activeProject });

  return (
    <Grid
      item
      container
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      wrap="nowrap"
      style={{
        padding: `16px 21px`,
        borderBottom: `1px solid #E0E0E0`,
      }}
    >
      <Grid item container>
        <RoutalTooltip title={intl.formatMessage({ id: `project_analytics.line_chart` })}>
          <Button
            style={{
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              padding: `4px 6px`,
              ...(chartType !== `line`
                ? {
                    backgroundColor: `#E9E9E9`,
                  }
                : {}),
            }}
            styleType={chartType === `line` ? `primary` : `secondary`}
            onClick={() => {
              urlParams.set(`chart_type`, `line`);
              rootNavigate(`${getRootLocation().pathname}?${urlParams.toString()}`);
            }}
          >
            <LineChartIcon style={{ color: chartType === `line` ? `white` : `#AAAAAA` }} />
          </Button>
        </RoutalTooltip>

        <RoutalTooltip title={intl.formatMessage({ id: `project_analytics.bar_chart` })}>
          <Button
            style={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              padding: `4px 6px`,
              ...(chartType !== `bar`
                ? {
                    backgroundColor: `#E9E9E9`,
                  }
                : {}),
            }}
            styleType={chartType === `bar` ? `primary` : `secondary`}
            onClick={() => {
              urlParams.set(`chart_type`, `bar`);
              rootNavigate(`${getRootLocation().pathname}?${urlParams.toString()}`);
            }}
          >
            <BarChartIcon style={{ color: chartType === `bar` ? `white` : `#AAAAAA` }} />
          </Button>
        </RoutalTooltip>

        {showStackedOption ? (
          <>
            <HSpacer medium />

            <RoutalTooltip title={intl.formatMessage({ id: `project_analytics.stacked_chart` })}>
              <Button
                style={{
                  padding: `4px 6px`,
                  ...(!stackedChart
                    ? {
                        backgroundColor: `#E9E9E9`,
                      }
                    : {}),
                }}
                styleType={stackedChart ? `primary` : `secondary`}
                onClick={() => {
                  urlParams.set(`stacked_chart`, `${!stackedChart}`);
                  rootNavigate(`${getRootLocation().pathname}?${urlParams.toString()}`);
                }}
              >
                <StackChartIcon
                  style={{
                    color: stackedChart ? `white` : `#AAAAAA`,
                  }}
                />
              </Button>
            </RoutalTooltip>
          </>
        ) : null}
        {showCumulativeOption ? (
          <>
            <HSpacer medium />

            <RoutalTooltip title={intl.formatMessage({ id: `project_analytics.cumulative_chart` })}>
              <Button
                style={{
                  padding: `4px 6px`,
                  ...(!cumulativeChart
                    ? {
                        backgroundColor: `#E9E9E9`,
                      }
                    : {}),
                }}
                styleType={cumulativeChart ? `primary` : `secondary`}
                onClick={() => {
                  urlParams.set(`cumulative_chart`, `${!cumulativeChart}`);
                  rootNavigate(`${getRootLocation().pathname}?${urlParams.toString()}`);
                }}
              >
                <CumulativeChartIcon
                  style={{
                    color: cumulativeChart ? `white` : `#AAAAAA`,
                  }}
                />
              </Button>
            </RoutalTooltip>
          </>
        ) : null}
      </Grid>

      {showBinSelector ? (
        <Grid item>
          <RoutalSelect
            id="bin-type-select"
            containerStyle={{
              width: `120px`,
            }}
            popoverProps={{
              options: ([`day`, `week`, `month`, `quarter`] as BinTypes[]).map(
                (bin): RoutalSelectMultiSelectOption => ({
                  key: bin,
                  onClick: () => {
                    urlParams.set(`bin_type`, `${bin}`);
                    rootNavigate(`${getRootLocation().pathname}?${urlParams.toString()}`);
                  },
                  content: <Text style={{ cursor: `pointer` }} intlId={`project_analytics.bin_type.${bin}`} />,
                })
              ),
            }}
          >
            <Text
              variant="body"
              intlId={`project_analytics.bin_type.${binType}`}
              style={{
                cursor: `pointer`,
                width: `100%`,
                textAlign: `justify`,
              }}
            />
          </RoutalSelect>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default ChartHeaderFilters;
